import {useEffect, useRef, useState} from 'react';
import {canOfferOnPage, canSuggestInstall, installationPlatform, isStandalone, readInstallHistory, writeInstallHistory, SESSION_KEY, SUGGEST_AFTER_MS} from './installPolicy';
import './installApp.css';

export default function InstallApp() {
  const [suggestion, setSuggestion] = useState(false);
  const [instructions, setInstructions] = useState(false);
  const [busy, setBusy] = useState(false);
  const [nativeReady, setNativeReady] = useState(false);
  const deferred = useRef(null);
  const dialog = useRef(null);
  const returnFocus = useRef(null);
  const platform = installationPlatform();

  useEffect(() => {
    const media = window.matchMedia('(display-mode: standalone)');
    const syncInstalledMode = () => {
      document.body.toggleAttribute('data-mokaab-installed', isStandalone());
      if (isStandalone()) { setSuggestion(false); setInstructions(false); }
    };
    const ready = event => {
      event.preventDefault();
      deferred.current = event;
      setNativeReady(true);
      const history = readInstallHistory();
      if (history?.installed) writeInstallHistory({...history, installed: false});
    };
    const installed = () => {
      deferred.current = null;
      setNativeReady(false);
      writeInstallHistory({...readInstallHistory(), installed: true});
      document.body.setAttribute('data-mokaab-installed', '');
      setSuggestion(false); setInstructions(false);
    };
    const manual = event => {
      const trigger = event.target.closest('[data-mokaab-install]');
      if (!trigger || isStandalone()) return;
      returnFocus.current = trigger;
      setSuggestion(false); setInstructions(true);
    };
    syncInstalledMode();
    window.addEventListener('beforeinstallprompt', ready);
    window.addEventListener('appinstalled', installed);
    document.addEventListener('click', manual);
    media.addEventListener('change', syncInstalledMode);
    let visibleMs = 0;
    let offered = false;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') visibleMs += 1000;
      if (dialog.current?.open) return;
      if (!canOfferOnPage()) { setSuggestion(false); return; }
      if (offered || visibleMs < SUGGEST_AFTER_MS || isStandalone()) return;
      const history = readInstallHistory();
      if (!canSuggestInstall(history)) return;
      try {
        if (sessionStorage.getItem(SESSION_KEY)) return;
        sessionStorage.setItem(SESSION_KEY, 'shown');
      } catch { return; }
      if (!writeInstallHistory({...history, count: history.count + 1, lastShown: Date.now()})) return;
      offered = true;
      setSuggestion(true);
    }, 1000);
    return () => {
      clearInterval(timer);
      window.removeEventListener('beforeinstallprompt', ready);
      window.removeEventListener('appinstalled', installed);
      document.removeEventListener('click', manual);
      media.removeEventListener('change', syncInstalledMode);
    };
  }, []);

  useEffect(() => {
    if (instructions) dialog.current?.showModal();
    else if (dialog.current?.open) dialog.current.close();
  }, [instructions]);

  const closeInstructions = () => {
    setInstructions(false);
    if (returnFocus.current?.isConnected) returnFocus.current.focus({preventScroll: true});
  };
  const install = async event => {
    if (!instructions) returnFocus.current = event.currentTarget;
    const prompt = deferred.current;
    if (!prompt) { setInstructions(true); return; }
    setSuggestion(false);
    deferred.current = null;
    setNativeReady(false);
    setBusy(true);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === 'accepted') setInstructions(false);
    } catch { setInstructions(true); }
    finally { setBusy(false); }
  };

  return <>
    {suggestion && <aside className="mki-suggestion" dir="rtl" aria-label="نصب مکعب">
      <img src="/pwa/icon-192.png" width="48" height="48" alt="" />
      <div><strong>مکعب، روی صفحهٔ شما</strong><p>با یک لمس، دوباره به حساب‌تون برگردین.</p></div>
      <button className="mki-dismiss" type="button" aria-label="فعلاً نه" onClick={() => setSuggestion(false)}>×</button>
      <button className="mki-primary" type="button" onClick={install} disabled={busy}>افزودن مکعب</button>
    </aside>}
    <dialog className="mki-dialog" dir="rtl" ref={dialog} aria-labelledby="mki-title" onCancel={closeInstructions} onClose={closeInstructions}>
      <header><img src="/pwa/icon-192.png" width="52" height="52" alt="" /><h2 id="mki-title">مکعب همیشه دم دست‌تون</h2><button className="mki-dismiss" type="button" aria-label="بستن راهنمای نصب" onClick={closeInstructions}>×</button></header>
      <p>آیکن مکعب رو به صفحهٔ گوشی یا برنامه‌های لپ‌تاپ‌تون اضافه کنین.</p>
      {platform === 'ios' ? <ol><li>این صفحه رو در <b>Safari</b> باز کنین.</li><li>از منوی اشتراک‌گذاری <b lang="en">Share</b>، گزینهٔ <b lang="en">Add to Home Screen</b> رو بزنین.</li><li>اگر گزینهٔ <b lang="en">Open as Web App</b> هست، روشنش کنین و <b lang="en">Add</b> رو بزنین.</li></ol>
        : platform === 'android' ? <ol><li>از منوی مرورگر، <b>نصب برنامه</b> یا <b lang="en">Add to Home screen</b> رو انتخاب کنین.</li><li>در پنجرهٔ بازشده، نصب یا افزودن رو تأیید کنین.</li></ol>
          : <ol><li>در Chrome یا Edge، روی آیکن نصب کنار نوار آدرس یا گزینهٔ نصب این سایت در منوی مرورگر کلیک کنین.</li><li>نصب رو تأیید کنین تا مکعب از فهرست برنامه‌های دستگاه هم باز بشه.</li></ol>}
      <p className="mki-hint">برای استفاده از حساب و دیدن نرخ تازه، به اینترنت نیاز دارین.</p>
      {nativeReady && <button className="mki-primary" type="button" disabled={busy} onClick={install}>{busy ? 'در حال باز کردن پنجرهٔ نصب…' : 'نصب مکعب'}</button>}
      <button className="mki-understood" type="button" onClick={closeInstructions}>متوجه شدم</button>
    </dialog>
  </>;
}
